import { Directive, effect, ElementRef, inject, input, Renderer2 } from '@angular/core';

import {
  ProductUpdatesConfig,
  PRODUCT_UPDATES_CONFIG,
} from '../config/product-updates-config.provider';

/**
 * Directive that conditionally adds a data-testid attribute to elements.
 * The attribute is only rendered in non-production environments (local, staging, rc).
 *
 * Usage:
 * ```html
 * <button puTestId="submit-button">Submit</button>
 * <nav puTestId="main-navigation" [testData]="{ category: 'news', id: 42 }">...</nav>
 * ```
 *
 * In non-production: renders as `<button data-testid="submit-button">`
 * With testData: renders as `<nav data-testid="main-navigation" data-testid-category="news" data-testid-id="42">`
 * In production: renders as `<button>` / `<nav>` (no attributes)
 */
@Directive({ selector: '[puTestId]' })
export class E2ETestDirective {
  private readonly config = inject<ProductUpdatesConfig>(PRODUCT_UPDATES_CONFIG);
  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);

  public readonly puTestId = input.required<string>();
  public readonly testData = input<Record<string, string | number | null | undefined>>();

  constructor() {
    if (this.config.showTestIds) {
      this.createDataEffect();
    }
  }

  private createDataEffect(): void {
    const el = this.elementRef.nativeElement;

    effect(() => {
      this.renderer.setAttribute(el, 'data-testid', this.puTestId());
    });

    effect(onCleanup => {
      const data = this.testData();
      const keys: string[] = [];
      if (data) {
        for (const [key, value] of Object.entries(data)) {
          if (value != null) {
            this.renderer.setAttribute(el, `data-testid-${key}`, `${value}`);
            keys.push(key);
          }
        }
      }
      onCleanup(() => {
        for (const key of keys) {
          this.renderer.removeAttribute(el, `data-testid-${key}`);
        }
      });
    });
  }
}
